

import React, { useState } from 'react';
import { auth } from '../services/firebase';
import * as adminService from '../services/adminService';
import { runRetroactiveWordCountForNickHull, assignNumbersToExistingAssessments, getAllAssessments } from '../services/assessmentService';
import { DEFAULT_MODEL_ID, MODELS } from '../constants';
import Button from './Button';
import TextArea from './TextArea';
import TextInput from './TextInput';
import { getAdminNotifications, clearAdminNotifications } from '../services/adminNotifications';
import { createSnapshotAndDownload, listSnapshots, deleteSnapshot, downloadSnapshotById, SnapshotMeta } from '../services/snapshotService';

const AdminDashboard = ({ onBack }) => {
  const [essayText, setEssayText] = useState('');
  const [rubric, setRubric] = useState('');
  const [yearLevel, setYearLevel] = useState('');
  const [criteriaBands, setCriteriaBands] = useState([{ id: Date.now(), name: '', band: '' }]);
  const [overallScore, setOverallScore] = useState('');
  const [explanation, setExplanation] = useState('');

  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  
  // Word count processing state
  const [isProcessingWordCounts, setIsProcessingWordCounts] = useState(false);
  const [wordCountProgress, setWordCountProgress] = useState('');
  const [wordCountResults, setWordCountResults] = useState<any>(null);
  
  // Assessment numbering state
  const [isAssigningNumbers, setIsAssigningNumbers] = useState(false);
  const [numberingResults, setNumberingResults] = useState<{ updatedCount: number } | null>(null);

  // Notifications
  const [notifications, setNotifications] = useState([]);

  // Snapshots
  const [assessments, setAssessments] = useState<any[]>([]);
  const [selectedAssessmentIds, setSelectedAssessmentIds] = useState<string[]>([]);
  const [snapshots, setSnapshots] = useState<SnapshotMeta[]>([]);
  const [isLoadingSnapshots, setIsLoadingSnapshots] = useState(false);
  const [isSnapshotting, setIsSnapshotting] = useState(false);
  const [snapshotStatus, setSnapshotStatus] = useState('');
  const [snapshotError, setSnapshotError] = useState<string | null>(null);
  const [snapshotModelId, setSnapshotModelId] = useState(DEFAULT_MODEL_ID);

  const handleAddCriterion = () => {
    setCriteriaBands([...criteriaBands, { id: Date.now(), name: '', band: '' }]);
  };

  const refreshNotifications = () => {
    setNotifications(getAdminNotifications());
  };

  const handleClearNotifications = () => {
    clearAdminNotifications();
    refreshNotifications();
  };

  React.useEffect(() => {
    refreshNotifications();
  }, []);

  const loadAssessmentsForAdmin = async () => {
    if (!auth.currentUser) return;
    try {
      const items = await getAllAssessments(auth.currentUser.uid);
      setAssessments(items || []);
    } catch (err) {
      console.error('Failed to load assessments for snapshots', err);
    }
  };

  const loadSnapshotsForAdmin = async () => {
    if (!auth.currentUser) return;
    setIsLoadingSnapshots(true);
    try {
      const items = await listSnapshots(auth.currentUser.uid);
      setSnapshots(items || []);
    } catch (err: any) {
      setSnapshotError(err?.message || 'Failed to load snapshots');
    } finally {
      setIsLoadingSnapshots(false);
    }
  };

  React.useEffect(() => {
    loadAssessmentsForAdmin();
    loadSnapshotsForAdmin();
  }, []);

  const handleRemoveCriterion = (id) => {
    if (criteriaBands.length > 1) {
      setCriteriaBands(criteriaBands.filter(c => c.id !== id));
    }
  };

  const handleCriterionChange = (id, field, value) => {
    setCriteriaBands(criteriaBands.map(c => (c.id === id ? { ...c, [field]: value } : c)));
  };

  const toggleAssessmentSelection = (id: string) => {
    setSelectedAssessmentIds(prev =>
      prev.includes(id) ? prev.filter(item => item !== id) : [...prev, id]
    );
  };

  const selectAllAssessments = () => {
    setSelectedAssessmentIds(assessments.map((a: any) => a.id));
  };

  const formatSnapshotDate = (value: any) => {
    const date = (() => {
      if (!value) return null;
      if (value instanceof Date) return value;
      if (typeof value.toDate === 'function') return value.toDate();
      if (typeof value === 'object' && 'seconds' in value) return new Date((value as any).seconds * 1000);
      if (typeof value === 'string' || typeof value === 'number') {
        const d = new Date(value);
        return isNaN(d.getTime()) ? null : d;
      }
      return null;
    })();
    return date ? date.toLocaleString() : 'N/A';
  };

  const handleCreateSnapshot = async () => {
    if (!auth.currentUser) {
      setSnapshotError('User not authenticated.');
      return;
    }
    if (selectedAssessmentIds.length === 0) {
      setSnapshotError('Select at least one assessment to include.');
      return;
    }
    if (!snapshotModelId) {
      setSnapshotError('Select an AI model for the CSV export.');
      return;
    }
    setSnapshotError(null);
    setIsSnapshotting(true);
    setSnapshotStatus('Starting CSV snapshot...');
    try {
      await createSnapshotAndDownload(
        auth.currentUser.uid,
        selectedAssessmentIds,
        auth.currentUser.email || 'admin',
        setSnapshotStatus,
        { format: 'csv', selectedModelId: snapshotModelId }
      );
      setSnapshotStatus('Download started.');
      await loadSnapshotsForAdmin();
    } catch (err: any) {
      console.error('Snapshot creation failed', err);
      setSnapshotError(err?.message || 'Snapshot creation failed.');
    } finally {
      setIsSnapshotting(false);
    }
  };

  const handleDownloadSnapshot = async (snapshotId: string) => {
    if (!auth.currentUser) return;
    setSnapshotStatus('Preparing download...');
    try {
      await downloadSnapshotById(snapshotId, auth.currentUser.uid, setSnapshotStatus);
      setSnapshotStatus('Download started.');
    } catch (err: any) {
      console.error('Snapshot download failed', err);
      setSnapshotError(err?.message || 'Failed to download snapshot.');
    }
  };

  const handleDeleteSnapshot = async (snapshotId: string) => {
    if (!auth.currentUser) return;
    setSnapshotError(null);
    try {
      await deleteSnapshot(snapshotId);
      await loadSnapshotsForAdmin();
    } catch (err: any) {
      console.error('Snapshot delete failed', err);
      setSnapshotError(err?.message || 'Failed to delete snapshot.');
    }
  };

  const resetForm = () => {
    setEssayText('');
    setRubric('');
    setYearLevel('');
    setCriteriaBands([{ id: Date.now(), name: '', band: '' }]);
    setOverallScore('');
    setExplanation('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setSuccessMessage(null);

    const areCriteriaValid = criteriaBands.every(c => c.name.trim() !== '' && c.band.trim() !== '');

    if (!essayText.trim() || !rubric.trim() || !yearLevel || !overallScore.trim() || !areCriteriaValid) {
      setError("Essay, Rubric, Year Level, Overall Score, and all Criterion fields are required.");
      return;
    }

    setIsLoading(true);
    try {
      const exemplarData = { 
        essayText, 
        rubric, 
        yearLevel,
        criteriaBands: criteriaBands.map(({ id, ...rest }) => rest), // Remove client-side id before sending
        overallScore,
        explanation
      };
      await adminService.addTrainingExemplar(exemplarData, auth.currentUser.uid);
      setSuccessMessage("Training exemplar was successfully saved!");
      resetForm();
    } catch (err) {
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  };
  
  const canSubmit = essayText.trim() && rubric.trim() && yearLevel && overallScore.trim() && criteriaBands.every(c => c.name.trim() !== '' && c.band.trim() !== '') && !isLoading;

  // Word count processing handler
  const handleRetroactiveWordCount = async () => {
    setIsProcessingWordCounts(true);
    setWordCountProgress('Initializing...');
    setWordCountResults(null);
    setError(null);
    setSuccessMessage(null);

    try {
      const result = await runRetroactiveWordCountForNickHull(
        (current, total, essayId, status) => {
          const percentage = total > 0 ? Math.round((current / total) * 100) : 0;
          setWordCountProgress(`Processing essay ${current} of ${total} (${percentage}%) - ${status}`);
        }
      );
      
      setWordCountResults(result);
      setWordCountProgress('');
      
      if (result.processed > 0) {
        setSuccessMessage(`Successfully processed word counts for ${result.processed} essays using Gemini LLM!`);
      } else if (result.total === 0) {
        setSuccessMessage('No essays found for the specified user.');
      } else {
        setSuccessMessage('All essays already had word counts - no processing needed.');
      }
      
    } catch (err: any) {
      setError(`Word count processing failed: ${err.message}`);
      setWordCountProgress('');
    } finally {
      setIsProcessingWordCounts(false);
    }
  };

  // Assessment numbering handler
  const handleAssignAssessmentNumbers = async () => {
    if (!auth.currentUser) {
      setError('User not authenticated');
      return;
    }

    setIsAssigningNumbers(true);
    setNumberingResults(null);
    setError(null);
    setSuccessMessage(null);

    try {
      const updatedCount = await assignNumbersToExistingAssessments(auth.currentUser.uid);
      
      setNumberingResults({ updatedCount });
      
      if (updatedCount > 0) {
        setSuccessMessage(`Successfully assigned numbers to ${updatedCount} assessments!`);
      } else {
        setSuccessMessage('All assessments already have numbers - no updates needed.');
      }
      
    } catch (err: any) {
      setError(`Assessment numbering failed: ${err.message}`);
    } finally {
      setIsAssigningNumbers(false);
    }
  };

  return (
    <div className="w-full p-6 bg-white rounded-lg shadow-xl space-y-6">
      <div className="flex justify-between items-start">
        <div>
          <h2 className="text-2xl font-bold text-secondary-800">Admin Dashboard</h2>
          <p className="text-sm text-secondary-600 mt-1">Upload training exemplars to improve AI marking accuracy.</p>
        </div>
        <Button onClick={onBack} variant="secondary" disabled={isLoading}>
          Back to App
        </Button>
      </div>

      {/* Notifications */}
      <div className="border border-secondary-200 rounded-lg p-4 bg-secondary-50 space-y-3">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-lg font-semibold text-secondary-800">Notifications</h3>
            <p className="text-sm text-secondary-600">System alerts (stored locally for admins).</p>
          </div>
          <div className="flex gap-2">
            <Button variant="secondary" size="sm" onClick={refreshNotifications}>Refresh</Button>
            <Button variant="secondary" size="sm" onClick={handleClearNotifications}>Clear</Button>
          </div>
        </div>
        {notifications.length === 0 ? (
          <p className="text-sm text-secondary-600">No notifications.</p>
        ) : (
          <div className="space-y-2 max-h-64 overflow-auto">
            {notifications.map((n) => (
              <div key={n.id} className="bg-white border border-secondary-200 rounded-md p-3">
                <div className="flex items-center justify-between">
                  <span className="text-xs font-semibold uppercase tracking-wide text-secondary-700">{n.type}</span>
                  <span className="text-xs text-secondary-500">{new Date(n.createdAt).toLocaleString()}</span>
                </div>
                <div className="text-sm text-secondary-900 mt-1">{n.message}</div>
                {n.meta && (
                  <pre className="mt-2 text-xs text-secondary-800 bg-secondary-50 border border-secondary-200 rounded p-2 overflow-auto">
                    {JSON.stringify(n.meta, null, 2)}
                  </pre>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Snapshots */}
      <div className="border border-blue-200 rounded-lg p-4 bg-blue-50 space-y-4">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-lg font-semibold text-blue-800">Results Snapshots</h3>
            <p className="text-sm text-blue-700">Admin-only CSV export of AI vs teacher marks for the selected model. One row per essay.</p>
          </div>
          <div className="flex gap-2">
            <Button variant="secondary" size="sm" onClick={selectAllAssessments} disabled={assessments.length === 0}>Select All</Button>
            <Button variant="secondary" size="sm" onClick={() => setSelectedAssessmentIds([])} disabled={selectedAssessmentIds.length === 0}>Clear</Button>
          </div>
        </div>

        <div className="bg-white border border-blue-100 rounded-md p-3 max-h-48 overflow-auto">
          {assessments.length === 0 ? (
            <p className="text-sm text-secondary-600">No assessments found. Create assessments first.</p>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              {assessments.map((a: any) => (
                <label key={a.id} className="flex items-center gap-2 text-sm text-secondary-800">
                  <input
                    type="checkbox"
                    className="rounded border-secondary-300"
                    checked={selectedAssessmentIds.includes(a.id)}
                    onChange={() => toggleAssessmentSelection(a.id)}
                  />
                  <span>{a.name || a.title || `Assessment ${a.assessmentNumber || ''}`}</span>
                </label>
              ))}
            </div>
          )}
        </div>

        <div className="flex flex-col sm:flex-row sm:items-center gap-3">
          <label htmlFor="snapshot-model" className="text-sm font-medium text-secondary-700">
            AI model for CSV export
          </label>
          <select
            id="snapshot-model"
            value={snapshotModelId}
            onChange={(e) => setSnapshotModelId(e.target.value)}
            disabled={isSnapshotting}
            className="block w-full sm:w-80 rounded-md border-0 py-1.5 text-secondary-900 shadow-sm ring-1 ring-inset ring-secondary-300 focus:ring-2 focus:ring-inset focus:ring-primary-600 sm:text-sm sm:leading-6"
          >
            {Object.values(MODELS).map((model) => (
              <option key={model.id} value={model.id}>
                {model.name}
              </option>
            ))}
          </select>
          <span className="text-xs text-secondary-600">Exports AI scores from this model only.</span>
        </div>

        {snapshotError && (
          <p className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-md p-2">{snapshotError}</p>
        )}
        {snapshotStatus && (
          <p className="text-sm text-blue-700 bg-blue-50 border border-blue-200 rounded-md p-2">{snapshotStatus}</p>
        )}

        <div className="flex items-center justify-between">
          <p className="text-sm text-secondary-700">Selected assessments: {selectedAssessmentIds.length}</p>
          <Button
            onClick={handleCreateSnapshot}
            disabled={isSnapshotting || selectedAssessmentIds.length === 0 || !auth.currentUser}
            isLoading={isSnapshotting}
            loadingText="Building..."
            variant="primary"
          >
            Create & Download CSV Snapshot
          </Button>
        </div>

        <div className="pt-2 border-t border-blue-100 space-y-2">
          <div className="flex items-center justify-between">
            <h4 className="text-md font-semibold text-blue-800">Saved Snapshots</h4>
            {isLoadingSnapshots && <span className="text-xs text-secondary-600">Loading...</span>}
          </div>
          {snapshots.length === 0 ? (
            <p className="text-sm text-secondary-600">No snapshots yet.</p>
          ) : (
            <div className="space-y-2 max-h-60 overflow-auto">
              {snapshots.map((s) => (
                <div key={s.id} className="bg-white border border-secondary-200 rounded-md p-3 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                  <div className="space-y-1">
                    <p className="text-sm font-semibold text-secondary-900">Snapshot {s.id}</p>
                    <p className="text-xs text-secondary-600">Created: {formatSnapshotDate(s.createdAt)}</p>
                    <p className="text-xs text-secondary-600">Assessments: {s.assessmentNames?.join(', ') || s.assessmentIds?.length || 0}</p>
                    <p className="text-xs text-secondary-600">
                      Model: {s.selectedModelId ? (MODELS[s.selectedModelId]?.name || s.selectedModelId) : `Multiple (${s.modelIds?.length || 0})`} | Format: {s.format === 'csv' ? 'CSV' : 'Excel'} | Rows: {s.rowCount ?? '-'}
                    </p>
                    <p className="text-xs text-secondary-600">Triggered by: {s.triggeredBy || 'Unknown'}</p>
                  </div>
                  <div className="flex gap-2">
                    <Button size="sm" variant="secondary" onClick={() => handleDownloadSnapshot(s.id)}>Download</Button>
                    <Button size="sm" variant="danger" onClick={() => handleDeleteSnapshot(s.id)}>Delete</Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6 pt-4 border-t border-secondary-200">
        <p className="text-sm text-secondary-700 bg-amber-50 border border-amber-200 p-3 rounded-md">
          <strong>How this works:</strong> The data you upload here will be stored in a knowledge base. In the future, this will be used in a RAG (Retrieval-Augmented Generation) process. When a new essay is marked, the AI will retrieve similar examples from this database to improve its context and provide more accurate, fair marking.
        </p>
        
        <TextArea
          label="Example Essay Text"
          id="exemplarEssay"
          value={essayText}
          onChange={(e) => setEssayText(e.target.value)}
          rows={12}
          placeholder="Paste the full text of a previously marked, high-quality example essay here."
          required
        />
        
        <TextArea
          label="Corresponding Marking Rubric"
          id="exemplarRubric"
          value={rubric}
          onChange={(e) => setRubric(e.target.value)}
          rows={8}
          placeholder="Paste the specific rubric that was used to mark the above essay."
          required
        />

        <div className="p-4 border border-secondary-200 rounded-lg space-y-4">
            <h3 className="text-lg font-medium text-secondary-800">Per-Criterion Bands</h3>
            {criteriaBands.map((criterion, index) => (
                <div key={criterion.id} className="flex items-end gap-4 p-3 bg-secondary-50 rounded-md">
                    <div className="flex-grow">
                      <TextInput
                          label={`Criterion ${index + 1} Name`}
                          id={`criterion-name-${criterion.id}`}
                          value={criterion.name}
                          onChange={(e) => handleCriterionChange(criterion.id, 'name', e.target.value)}
                          placeholder="e.g., Argumentation"
                          required
                      />
                    </div>
                    <div className="flex-grow">
                       <TextInput
                          label="Band Achieved"
                          id={`criterion-band-${criterion.id}`}
                          value={criterion.band}
                          onChange={(e) => handleCriterionChange(criterion.id, 'band', e.target.value)}
                          placeholder="e.g., Excellent, 8-10"
                          required
                      />
                    </div>
                    <Button 
                      type="button" 
                      onClick={() => handleRemoveCriterion(criterion.id)} 
                      variant="danger" 
                      size="sm"
                      disabled={criteriaBands.length <= 1}
                      className="mb-1"
                      aria-label={`Remove Criterion ${index + 1}`}
                    >
                      Remove
                    </Button>
                </div>
            ))}
            <Button type="button" onClick={handleAddCriterion} variant="secondary">
                + Add Criterion
            </Button>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <TextInput
            label="Overall Score"
            id="exemplarOverallScore"
            type="text"
            value={overallScore}
            onChange={(e) => setOverallScore(e.target.value)}
            placeholder="e.g., 18 or 18/20"
            required
          />
          
          <div>
            <label htmlFor="yearLevel" className="block text-sm font-medium leading-6 text-secondary-900">
                Year Level
            </label>
            <div className="mt-2">
                <select
                    id="yearLevel"
                    name="yearLevel"
                    value={yearLevel}
                    onChange={(e) => setYearLevel(e.target.value)}
                    required
                    className="block w-full rounded-md border-0 py-1.5 text-secondary-900 shadow-sm ring-1 ring-inset ring-secondary-300 focus:ring-2 focus:ring-inset focus:ring-primary-600 sm:text-sm sm:leading-6"
                >
                    <option value="" disabled>Select Year Level...</option>
                    <option value="Year 7">Year 7</option>
                    <option value="Year 8">Year 8</option>
                    <option value="Year 9">Year 9</option>
                    <option value="Year 10">Year 10</option>
                    <option value="Year 11">Year 11</option>
                    <option value="Year 12">Year 12</option>
                </select>
            </div>
          </div>
        </div>

         <TextArea
          label="Explanation (Optional)"
          id="exemplarExplanation"
          value={explanation}
          onChange={(e) => setExplanation(e.target.value)}
          rows={4}
          placeholder="If available, provide a brief explanation for why the essay received its score."
        />
        
        {successMessage && <p className="text-sm text-green-600 bg-green-50 p-3 rounded-md">{successMessage}</p>}
        {error && <p className="text-sm text-red-600 bg-red-50 p-3 rounded-md">{error}</p>}

        <div className="text-right pt-4">
          <Button type="submit" disabled={!canSubmit} isLoading={isLoading} loadingText="Saving..." size="lg">
            Save Exemplar to Knowledge Base
          </Button>
        </div>
      </form>

      {/* Retroactive Word Count Processing Section */}
      <div className="pt-6 border-t border-secondary-200">
        <h3 className="text-xl font-bold text-secondary-800 mb-4">Retroactive Word Count Processing</h3>
        <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg space-y-4">
          <p className="text-sm text-secondary-700">
            <strong>Retroactive Processing for nick.hull.42@gmail.com:</strong> This tool will process all existing essays 
            under the specified account through the Gemini LLM to calculate word counts and update the Firebase database.
          </p>
          
          {wordCountProgress && (
            <div className="bg-white border border-blue-300 rounded-md p-3">
              <p className="text-sm text-blue-800 font-medium">Processing Status:</p>
              <p className="text-sm text-blue-700">{wordCountProgress}</p>
            </div>
          )}
          
          {wordCountResults && (
            <div className="bg-white border border-green-300 rounded-md p-3 space-y-2">
              <p className="text-sm text-green-800 font-medium">Processing Results:</p>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                <div>
                  <span className="font-medium">Total Essays:</span> {wordCountResults.total}
                </div>
                <div>
                  <span className="font-medium">Processed:</span> {wordCountResults.processed}
                </div>
                <div>
                  <span className="font-medium">Skipped:</span> {wordCountResults.skipped}
                </div>
                <div>
                  <span className="font-medium">Errors:</span> {wordCountResults.errors}
                </div>
              </div>
              {wordCountResults.details && wordCountResults.details.length > 0 && (
                <div className="mt-3 max-h-40 overflow-y-auto">
                  <p className="text-sm font-medium text-green-800 mb-2">Detailed Results:</p>
                  {wordCountResults.details.map((detail, index) => (
                    <div key={index} className="text-xs text-secondary-600 py-1 border-b border-secondary-100 last:border-b-0">
                      <span className="font-medium">{detail.studentName}:</span>{' '}
                      {detail.wordCount !== undefined ? (
                        <span className="text-green-600">{detail.wordCount} words</span>
                      ) : detail.error ? (
                        <span className="text-red-600">Error - {detail.error}</span>
                      ) : (
                        <span className="text-secondary-500">Processing...</span>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
          
          <div className="text-right">
            <Button 
              onClick={handleRetroactiveWordCount}
              disabled={isProcessingWordCounts || isLoading}
              isLoading={isProcessingWordCounts}
              loadingText="Processing..."
              variant="primary"
            >
              Run Retroactive Word Count Processing
            </Button>
          </div>
        </div>
      </div>

      {/* Assessment Numbering Section */}
      <div className="pt-6 border-t border-secondary-200">
        <h3 className="text-xl font-bold text-secondary-800 mb-4">Assessment Numbering</h3>
        <div className="p-4 bg-purple-50 border border-purple-200 rounded-lg space-y-4">
          <p className="text-sm text-secondary-700">
            <strong>Fix Assessment Numbers:</strong> This tool will assign sequential numbers to existing assessments 
            that don't have assessment numbers. This fixes the "Assessment N/A" display issue.
          </p>
          
          {numberingResults && (
            <div className="bg-white border border-green-300 rounded-md p-3 space-y-2">
              <p className="text-sm text-green-800 font-medium">Numbering Results:</p>
              <div className="text-sm">
                <span className="font-medium">Assessments Updated:</span> {numberingResults.updatedCount}
              </div>
            </div>
          )}
          
          <div className="text-right">
            <Button 
              onClick={handleAssignAssessmentNumbers}
              disabled={isAssigningNumbers || isLoading}
              isLoading={isAssigningNumbers}
              loadingText="Assigning Numbers..."
              variant="primary"
            >
              Assign Numbers to Existing Assessments
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminDashboard;
